import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import ExportBar from "../../Components/Accounting/ExportBar";
import { RenderSecure } from "../../util/script/RenderSecure";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";

const TYPE_COLORS = { PV:"#ef4444", RV:"#10b981", JV:"#6366f1" };

const VoucherDetail = () => {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [financialYears, setFinancialYears] = useState([]);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { if (id) { fetchVoucher(); fetchFinancialYears(); } }, [id]);

  const fetchVoucher = async () => {
    setLoading(true);
    const res = await ajaxAccounting.getVoucher({ id: parseInt(id) });
    if (res.status === "OK") setVoucher(res.details);
    else { toast.error(res.message || "Voucher not found"); navigate("/accounting/vouchers"); }
    setLoading(false);
  };

  const fetchFinancialYears = async () => {
    const res = await ajaxAccounting.listFinancialYears({});
    if (res.status === "OK") setFinancialYears(res.details || []);
  };

  const handleApprove = async () => {
    const res = await ajaxAccounting.approveVoucher({ id: parseInt(id) });
    if (res.status === "OK") { toast.success("Voucher approved and posted!"); fetchVoucher(); }
    else toast.error(res.message);
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this draft voucher? This cannot be undone.")) return;
    setDeleting(true);
    const res = await ajaxAccounting.deleteVoucher({ id: parseInt(id) });
    if (res.status === "OK") { toast.success("Voucher deleted."); navigate("/accounting/vouchers"); }
    else { toast.error(res.message || "Failed to delete"); setDeleting(false); }
  };

  if (loading) return (
    <AppContainer title="Voucher" previousPageTitle="Vouchers">
      <div className="text-center py-5"><i className="fa-solid fa-spinner fa-spin fa-2x tx-primary"></i></div>
    </AppContainer>
  );

  if (!voucher) return null;

  const lines    = voucher.lines || [];
  const totalDr  = lines.reduce((s, l) => s + (parseFloat(l.debit)  || 0), 0);
  const totalCr  = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const color    = TYPE_COLORS[voucher.type] || "#6b7280";

  const exportCols = [
    { key:"account_code", label:"Account Code" },
    { key:"account_name", label:"Account" },
    { key:"description",  label:"Description" },
    { key:"debit",        label:"Debit" },
    { key:"credit",       label:"Credit" },
  ];

  const statusBadge = (s) => {
    const m = { draft:"secondary", approved:"info", posted:"success", void:"danger" };
    return <span className={`badge bg-${m[s]||"secondary"} px-3 py-1`}>{s?.toUpperCase()}</span>;
  };

  const entryYear = financialYears.find((y) => y.id === voucher.financial_year_id);
  const isYearOpen = entryYear?.status === "open";
  const canEdit = voucher.status === "draft" && financialYears.length > 0 && isYearOpen;

  return (
    <AppContainer
      title={`${voucher.type} — ${voucher.voucher_number || `V-${id}`}`}
      previousPageTitle="Vouchers"
      actions={[
        ...(voucher.status === "draft" ? [{
          label:"Approve & Post", icon:"fa-solid fa-check", className:"btn btn-success",
          onClick: handleApprove,
        }] : []),
        ...(canEdit ? [
          { label:"Edit", icon:"fa-solid fa-pen", className:"btn btn-outline-primary", onClick: () => navigate(`/accounting/vouchers/new?type=${voucher.type}&edit=${voucher.id}`) },
          { label: deleting ? "Deleting..." : "Delete", icon:"fa-solid fa-trash", className:"btn btn-outline-danger", onClick: handleDelete, disabled: deleting },
        ] : []),
        { label:"Print",         icon:"fa-solid fa-print",      className:"btn btn-outline-secondary", onClick:() => window.print() },
        { label:"All Vouchers",  icon:"fa-solid fa-arrow-left", className:"btn btn-light",             onClick:() => navigate("/accounting/vouchers") },
      ]}
    >
      <Toaster />

      {/* Header card */}
      <div className="card custom-card p-4 mb-3">
        <div className="row">
          <div className="col-lg-8">
            <div className="d-flex align-items-center gap-3 mb-3">
              <div style={{ width:"48px", height:"48px", borderRadius:"10px", background:color+"18", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <span className="fw-bold" style={{ color, fontSize:"1rem" }}>{voucher.type}</span>
              </div>
              <div>
                <h5 className="fw-bold mb-0">{voucher.voucher_number || `V-${id}`}</h5>
                <div className="d-flex align-items-center gap-2 mt-1">
                  {statusBadge(voucher.status)}
                  <span className="tx-12 tx-muted">{functions.formatDate(voucher.voucher_date)}</span>
                </div>
              </div>
            </div>

            {voucher.status === "draft" && entryYear && !isYearOpen && (
              <div className="alert alert-warning p-2 tx-12 mb-3">
                <i className="fa-solid fa-lock me-1"></i>
                This voucher's financial year ({entryYear.name}) is {entryYear.status} — editing and deleting are disabled.
              </div>
            )}

            <div className="row g-2 tx-13">
              <div className="col-sm-6">
                <span className="tx-muted">Description:</span>
                <div className="fw-semibold">{voucher.description}</div>
              </div>
              <div className="col-sm-6">
                <span className="tx-muted">Reference:</span>
                <div className="fw-semibold">{voucher.reference || "—"}</div>
              </div>
              {voucher.payee_name && (
                <div className="col-sm-6">
                  <span className="tx-muted">{voucher.type === "RV" ? "Received From:" : "Paid To:"}</span>
                  <div className="fw-semibold">{voucher.payee_name}</div>
                </div>
              )}
              {voucher.contact_name && (
                <div className="col-sm-6">
                  <span className="tx-muted">Contact:</span>
                  <div className="fw-semibold">{voucher.contact_name}</div>
                </div>
              )}
              {voucher.project_name && (
                <div className="col-sm-6">
                  <span className="tx-muted">Project:</span>
                  <div className="fw-semibold">{voucher.project_name}</div>
                </div>
              )}
              {voucher.notes && (
                <div className="col-12">
                  <span className="tx-muted">Notes:</span>
                  <div className="tx-12">{voucher.notes}</div>
                </div>
              )}
            </div>
          </div>

          <div className="col-lg-4">
            <div className="p-3 rounded" style={{ background:"#f8fafc", border:"1px solid #e5e7eb" }}>
              <div className="d-flex justify-content-between mb-2 tx-13">
                <span className="tx-muted">Total Debit</span>
                <span className="fw-bold tx-success">{functions.formatCurrency(totalDr)}</span>
              </div>
              <div className="d-flex justify-content-between mb-2 tx-13">
                <span className="tx-muted">Total Credit</span>
                <span className="fw-bold tx-danger">{functions.formatCurrency(totalCr)}</span>
              </div>
              <hr className="my-2" />
              <div className="d-flex justify-content-between tx-14">
                <span className="fw-semibold">Amount</span>
                <span className="fw-bold" style={{ color, fontSize:"1.1rem" }}>{functions.formatCurrency(voucher.total_amount || totalDr)}</span>
              </div>
              {voucher.created_by_name && (
                <div className="mt-2 tx-11 tx-muted">
                  Created by <strong>{voucher.created_by_name}</strong><br />
                  {voucher.created_at && new Date(voucher.created_at).toLocaleString("en-GB")}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Lines table */}
      <div className="card custom-card p-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="fw-semibold mb-0">Account Lines</h6>
          <ExportBar data={lines} columns={exportCols}
            filename={`voucher-${voucher.voucher_number || id}`}
            pdfTitle={`${voucher.type} — ${voucher.voucher_number || id}`}
            printCode="ACC-VOUCHER" exportCode="ACC-VOUCHER" />
        </div>
        <div className="table-responsive">
          <table className="table table-bordered table-sm">
            <thead className="bg-light">
              <tr><th>Code</th><th>Account</th><th>Description</th><th>Project</th><th className="text-end">Debit</th><th className="text-end">Credit</th></tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td><code className="tx-11">{l.account_code}</code></td>
                  <td className="tx-13">{l.account_name}</td>
                  <td className="tx-12">{l.description || "—"}</td>
                  <td className="tx-12">{l.project_name || "—"}</td>
                  <td className="text-end tx-success">{l.debit > 0 ? functions.formatCurrency(l.debit) : "—"}</td>
                  <td className="text-end tx-danger">{l.credit > 0 ? functions.formatCurrency(l.credit) : "—"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="fw-semibold bg-light">
              <tr>
                <td colSpan={4}>Totals</td>
                <td className="text-end tx-success">{functions.formatCurrency(totalDr)}</td>
                <td className="text-end tx-danger">{functions.formatCurrency(totalCr)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </AppContainer>
  );
};

export default VoucherDetail;
